/**
 * safeStr — Coerce any value to a renderable string.
 *
 * Prevents React Error #185 ("Objects are not valid as a React child")
 * when API responses return objects where strings are expected.
 *
 * Known dangerous fields: entity_name, entity_type, token_symbol,
 * counterparty_entity, from_entity_name, to_entity_name.
 *
 * Usage: {safeStr(data.entity_name)} instead of {data.entity_name}
 */
export function safeStr(v: any): string {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
        // Extract common name-like fields from API response objects
        return v.name || v.entity_name || v.label || v.symbol || v.value || '';
    }
    return String(v);
}

/**
 * cleanTokenSymbol — strip placeholder strings that leak into edge labels.
 *
 * The backend (tron.py) and old snapshot paths historically substituted
 * "UNKNOWN" / "Unknown" when TronGrid's token_info lacked a symbol field.
 * The Transfers tab cleans this via formatCurrency(); the canvas edge
 * labels concat raw, so the placeholder shows through. This helper is the
 * single point of truth for what counts as a "no real symbol" string.
 *
 * Returns '' for any placeholder, otherwise returns the original string
 * with whitespace trimmed (preserves case so USDT stays USDT).
 */
const _TOKEN_PLACEHOLDERS = new Set(['UNKNOWN', 'CLAUDE', '—', '-', '?', '???', 'NULL', 'NONE']);
export function cleanTokenSymbol(v: any): string {
    if (v == null) return '';
    const t = String(v).trim();
    if (!t) return '';
    if (_TOKEN_PLACEHOLDERS.has(t.toUpperCase())) return '';
    return t;
}

/**
 * toErrorMessage — coerce an arbitrary error / API detail value to a
 * plain string safe to render in JSX or pass to toast.
 *
 * FastAPI returns validation errors as `detail: [{loc, msg, type}, ...]`,
 * which rendered directly as JSX triggers React #300 ("Objects are not
 * valid as a React child"). This helper flattens the common shapes —
 * string, Error, {detail}, {msg}, arrays of those — into a readable line.
 *
 * Accepts either a raw error value or an axios-ish error whose
 * `.response.data.detail` / `.message` carry the server reply.
 */
export function toErrorMessage(err: any, fallback: string = 'Unknown error'): string {
    if (err == null) return fallback;
    if (typeof err === 'string') return err;
    if (typeof err === 'number' || typeof err === 'boolean') return String(err);

    // Axios-ish error: unwrap response body first, then .message
    const detail = err?.response?.data?.detail;
    if (detail !== undefined) {
        const asText = flattenDetail(detail);
        if (asText) return asText;
    }
    if (typeof err.message === 'string' && err.message) return err.message;

    // Bare detail payload (passed directly rather than axios error)
    const flat = flattenDetail(err);
    return flat || fallback;
}

function flattenDetail(d: any): string {
    if (d == null) return '';
    if (typeof d === 'string') return d;
    if (typeof d === 'number' || typeof d === 'boolean') return String(d);
    if (Array.isArray(d)) {
        return d.map(flattenDetail).filter(Boolean).join('; ');
    }
    if (typeof d === 'object') {
        if (typeof d.msg === 'string') return d.msg;
        if (typeof d.message === 'string') return d.message;
        if (typeof d.detail === 'string') return d.detail;
        if (d.detail) return flattenDetail(d.detail);
        try { return JSON.stringify(d); } catch { return ''; }
    }
    return String(d);
}
